import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Bot, FileText, Hash, MessageSquare, Pin } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { MessageItem } from "./MessageItem";
import { FilePreviewDialog, type PreviewFile } from "./TeamFilesLibrary";
import { TeamFilesPanel } from "./TeamFilesPanel";
import { formatDay } from "./format";
import { conversationLabel, isAdmin, type Conversation, type MeReady, type PinnedMessage } from "./types";

type Props = {
  me: MeReady;
  conversation: Conversation;
  mentionNames: readonly string[];
  onOpenThread: (messageId: string) => void;
};

function PinnedItems({ pins }: { pins: readonly PinnedMessage[] }) {
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  return (
    <aside className="pinned-items" aria-label={`${pins.length} pinned ${pins.length === 1 ? "item" : "items"}`}>
      <span className="pinned-items__label"><Pin size={14} /><strong>Pinned</strong></span>
      <div className="pinned-items__list">
        {pins.map(({ message }) => {
          const attachment = message.attachments[0];
          const label = attachment?.filename ?? message.content.plainText;
          return attachment?.url !== null && attachment !== undefined
            ? <button type="button" key={message.id} onClick={() => { setPreviewFile(attachment); }}><span>{label}</span><FileText size={12} /></button>
            : <span key={message.id}>{label}</span>;
        })}
      </div>
      <FilePreviewDialog file={previewFile} onClose={() => { setPreviewFile(null); }} />
    </aside>
  );
}

export function MessageList({ me, conversation, mentionNames, onOpenThread }: Props) {
  const [limit, setLimit] = useState(50);
  const [showHidden, setShowHidden] = useState(false);
  const result = useQuery(api.communication.listMessages, { conversationId: conversation.id, limit });
  const toggleReaction = useMutation(api.communication.toggleReaction);
  const editMessage = useMutation(api.communication.editMessage);
  const pins = useQuery(api.communication.listPins, { conversationId: conversation.id });
  const savedMessages = useQuery(api.communication.listSavedMessages, {});
  const togglePin = useMutation(api.communication.togglePin);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastCount = useRef(0);

  const allMessages = result === undefined ? [] : [...result.page].reverse();
  const messages = allMessages.filter(message => showHidden || !(message.hiddenByMe === true || message.hiddenForEveryone === true));
  const label = conversationLabel(conversation);
  const isTeamFiles = conversation.type !== "direct" && label.toLocaleLowerCase() === "team-files";

  // Keep the newest message in view unless the reader scrolled up to read history.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (scroll === null) return;
    if (isTeamFiles) scroll.scrollTop = 0;
    else if (stickToBottom.current || messages.length > 0 && lastCount.current === 0) scroll.scrollTop = scroll.scrollHeight;
    lastCount.current = messages.length;
  }, [messages.length, conversation.id, isTeamFiles]);

  useEffect(() => {
    const scroll = scrollRef.current;
    if (scroll === null) return;
    const onScroll = () => { stickToBottom.current = scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 80; };
    scroll.addEventListener("scroll", onScroll, { passive: true });
    return () => { scroll.removeEventListener("scroll", onScroll); };
  }, []);

  const isDirect = conversation.type === "direct";
  const pinnedIds = new Set((pins ?? []).map(({ message }) => message.id));
  const savedIds = new Set((savedMessages ?? []).map(({ message }) => message.id));

  if (isTeamFiles) {
    return <TeamFilesPanel conversationId={conversation.id} canManage={isAdmin(me.role)} />;
  }

  return (
    <>
      {pins !== undefined && pins.length > 0 ? <PinnedItems pins={pins} /> : null}
      <label className="hidden-message-toggle"><input type="checkbox" checked={showHidden} onChange={event => { setShowHidden(event.target.checked); }} /> Show hidden messages</label>
      <div className="conversation-scroll" ref={scrollRef}>
      {result?.hasMore === true ? (
        <div className="load-older"><button type="button" onClick={() => { stickToBottom.current = false; setLimit((current) => current + 100); }}>Load older messages</button></div>
      ) : (
        <div className="channel-intro">
          <div className="channel-intro__icon">{isDirect ? <MessageSquare size={20} /> : <Hash size={20} />}</div>
          <h2>{isDirect ? `This is your conversation with ${label}` : `Welcome to #${label}`}</h2>
          <p>{isDirect ? (conversation.partner?.kind === "openclaw" ? "Ask anything — your AI teammate answers every message here." : "Only the two of you can see these messages.") : conversation.topic ?? "Say hello and set a topic from the members panel."}</p>
        </div>
      )}
      {result === undefined ? <p className="loading-hint">Loading messages…</p> : null}
      {messages.map((message, index) => {
        const previous = messages[index - 1];
        const showDay = previous === undefined || formatDay(previous.createdAt) !== formatDay(message.createdAt);
        const compact = previous !== undefined && !showDay && previous.authorPrincipalId === message.authorPrincipalId && message.createdAt - previous.createdAt < 5 * 60 * 1000 && previous.replyCount === 0;
        return (
          <div key={message.id}>
            {showDay ? <div className="date-divider">{formatDay(message.createdAt)}</div> : null}
            <MessageItem
              message={message}
              compact={compact}
              mentionNames={mentionNames}
              canEdit={message.authorPrincipalId === me.principalId && message.deletedAt === undefined}
              canHideForEveryone={me.role === "owner" || me.role === "admin"}
              canPin={!isDirect && isAdmin(me.role)}
              pinned={pinnedIds.has(message.id)}
              saved={savedIds.has(message.id)}
              announcement={conversation.type === "announcement"}
              onReact={(emoji) => { void toggleReaction({ messageId: message.id, emoji }); }}
              onEdit={(plainText) => editMessage({ messageId: message.id, plainText })}
              onTogglePin={() => togglePin({ conversationId: conversation.id, messageId: message.id })}
              onOpenThread={() => { onOpenThread(message.id); }}
            />
          </div>
        );
      })}
      {result !== undefined && messages.length === 0 ? (
        <p className="loading-hint">{conversation.partner?.kind === "openclaw" ? <><Bot size={13} /> Nobody has said anything yet — try “hello”.</> : "Nobody has said anything yet."}</p>
      ) : null}
      </div>
    </>
  );
}
